/**
 * Input sanitization utilities to prevent XSS, injection attacks, and data corruption.
 * Apply these to all user inputs before storing in database or rendering.
 */

/** Strip HTML tags and script content from a string */
export function stripHtml(input: string): string {
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '');
}

/** Remove null bytes and other dangerous control characters */
function removeControlChars(input: string): string {
  // Keep newlines (\n), carriage returns (\r), and tabs (\t)
  // eslint-disable-next-line no-control-regex
  return input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
}

/** Sanitize a general text input (names, titles, short fields) */
export function sanitizeText(input: string, maxLength = 500): string {
  if (!input) return '';
  return removeControlChars(stripHtml(input)).trim().slice(0, maxLength);
}

/** Sanitize a longer text input (notes, descriptions, conversation text) */
export function sanitizeLongText(input: string, maxLength = 5000): string {
  if (!input) return '';
  return removeControlChars(stripHtml(input)).trim().slice(0, maxLength);
}

/** Sanitize an email address */
export function sanitizeEmail(input: string): string {
  if (!input) return '';
  // Strip everything except valid email chars, trim, lowercase
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.@+_-]/gi, '')
    .slice(0, 254);
}

/** Sanitize a phone number */
export function sanitizePhone(input: string): string {
  if (!input) return '';
  // Allow digits, spaces, hyphens, parens, plus, dots
  return input
    .trim()
    .replace(/[^0-9+\-().\s]/g, '')
    .slice(0, 30);
}

/** Sanitize a numeric value (for currency/deal values) */
export function sanitizeNumeric(input: string): string {
  if (!input) return '';
  // Allow digits, decimal point, minus sign
  return input.replace(/[^0-9.\-]/g, '').slice(0, 20);
}

/** Sanitize an object's string values recursively (for custom_fields etc.) */
export function sanitizeObject(obj: Record<string, any>, maxValueLength = 1000): Record<string, any> {
  const sanitized: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const cleanKey = sanitizeText(key, 100);
    if (typeof value === 'string') {
      sanitized[cleanKey] = sanitizeText(value, maxValueLength);
    } else if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
      sanitized[cleanKey] = value;
    } else if (typeof value === 'object' && value !== null) {
      sanitized[cleanKey] = sanitizeObject(value, maxValueLength);
    }
  }
  return sanitized;
}
